// ── marketPrices.js — KAMIS 시장 평균가 fetch + 매칭 ────────────────────
// /api/market-prices 호출 → 정규화된 KAMIS 데이터 반환.
// 사용자 단가표(prices)의 품목명과 fuzzy 매칭해서 priceCode → KAMIS item 연결.
// 매칭 전 동의어 사전(synonyms.js)으로 별명 → 표준명 변환.

import { normalizeFoodName } from './synonyms';

// ── 정규화 헬퍼 ────────────────────────────────────────────────────────────
function normalize(text) {
  return String(text || '')
    .replace(/\s+/g, '')
    .replace(/[()[\]{}<>]/g, '')
    .toLowerCase();
}

let CACHE = null;
let CACHE_TS = 0;
const CLIENT_TTL = 6 * 60 * 60 * 1000;  // 6시간 (서버 24시간보다 짧게)

/**
 * KAMIS 시장 평균가 fetch (메모리 캐시 6시간)
 * @returns {Promise<{ok, updatedAt, items, error?}>}
 */
export async function fetchMarketPrices() {
  const now = Date.now();
  if (CACHE && now - CACHE_TS < CLIENT_TTL) return CACHE;

  try {
    const res = await fetch('/api/market-prices');
    const data = await res.json();
    if (!data.ok) return { ok: false, error: data.error || 'fetch failed', items: [] };
    CACHE = data;
    CACHE_TS = now;
    return data;
  } catch (e) {
    return { ok: false, error: e.message, items: [] };
  }
}

/**
 * 단가표 품목 ↔ KAMIS 시장가 매칭
 * @param {object} priceItem  단가표 항목 { name, code, ... }
 * @param {Array} kamisItems  KAMIS 정규화된 items 배열
 * @param {object} options    { preferRank: '상품'|'중품' (기본 '상품') }
 * @returns {object|null}     매칭된 KAMIS item 또는 null
 */
export function matchMarketItem(priceItem, kamisItems, options = {}) {
  if (!priceItem?.name || !Array.isArray(kamisItems)) return null;
  const { preferRank = '상품' } = options;
  // 동의어 사전 적용: "백오이" → "오이"
  const standardized = normalizeFoodName(priceItem.name);
  const target = normalize(standardized);
  if (!target) return null;

  // 1차: itemname 또는 kindname에 표준명이 정확히 포함
  let candidates = kamisItems.filter(k => {
    const kn = normalize(k.kindname);
    const inN = normalize(k.itemname);
    return (kn && kn.includes(target)) || (inN && inN.includes(target)) ||
           (kn && target.includes(kn) && kn.length >= 2);
  });

  // 표준명으로 못 찾으면 원본으로 한 번 더 시도
  if (candidates.length === 0 && standardized !== priceItem.name) {
    const orig = normalize(priceItem.name);
    candidates = kamisItems.filter(k => {
      const kn = normalize(k.kindname);
      const inN = normalize(k.itemname);
      return (kn && kn.includes(orig)) || (inN && inN.includes(orig));
    });
  }

  if (candidates.length === 0) return null;

  // 2차: 단가표 이름과 가장 유사한 것 (글자수 차이 적은 순)
  candidates.sort((a, b) => {
    const an = normalize(a.kindname || a.itemname);
    const bn = normalize(b.kindname || b.itemname);
    const aDiff = Math.abs(an.length - target.length);
    const bDiff = Math.abs(bn.length - target.length);
    return aDiff - bDiff;
  });

  // 3차: 우선 등급('상품') 선호
  const sameRank = candidates.find(c => c.rank === preferRank);
  return sameRank || candidates[0];
}

/**
 * 단가표 항목의 1kg 환산 가격 계산
 * @param {object} priceItem  { price, net_g }
 * @returns {number}  원/kg (net_g 없으면 0)
 */
export function userPricePerKg(priceItem) {
  if (!priceItem?.price || !priceItem?.net_g) return 0;
  const kg = Number(priceItem.net_g) / 1000;
  if (kg <= 0) return 0;
  return Math.round(Number(priceItem.price) / kg);
}

/**
 * 단가표 vs 시장가 비교
 * @returns {{ userKg, marketKg, diffPct, status }}
 *   status: 'cheap' (시장가보다 싸게 사고있음) | 'fair' | 'expensive' (비싸게 사고있음)
 */
export function compareUserVsMarket(priceItem, kamisItem) {
  const userKg = userPricePerKg(priceItem);
  const marketKg = kamisItem?.pricePerKg || 0;
  if (userKg === 0 || marketKg === 0) {
    return { userKg, marketKg, diffPct: null, status: 'unknown' };
  }
  const diffPct = ((userKg - marketKg) / marketKg) * 100;
  let status = 'fair';
  if (diffPct < -10) status = 'cheap';
  else if (diffPct > 15) status = 'expensive';
  return {
    userKg,
    marketKg,
    diffPct: Math.round(diffPct * 10) / 10,
    status,
  };
}
